import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { checkAuth, logout, User, AuthStatus, handleTokenFromUrl } from '../services/auth'

interface AuthContextType {
  isAuthenticated: boolean
  user?: User
  loading: boolean
  error?: string
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<{
    isAuthenticated: boolean
    user?: User
    loading: boolean
    error?: string
  }>({
    isAuthenticated: false,
    loading: true,
  })

  const refreshAuth = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: undefined }))
      const status: AuthStatus = await checkAuth()
      
      setAuthState({
        isAuthenticated: status.isAuthenticated,
        user: status.user,
        loading: false,
        error: status.error,
      })
    } catch (error) {
      console.error('Auth refresh failed:', error)
      setAuthState({
        isAuthenticated: false,
        loading: false,
        error: 'Failed to check authentication',
      })
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      // logout() redirects, so this won't be reached
    } catch (error) {
      console.error('Logout failed:', error)
      // Force refresh to clear state
      await refreshAuth()
    }
  }

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 Initializing auth context')
      
      // Handle token from URL fragment if present (after OAuth redirect)
      const tokenExtracted = handleTokenFromUrl()
      console.log('🔍 Token extracted from URL:', tokenExtracted)
      
      // If token was just extracted, wait a moment for localStorage to be updated
      // and for any browser processing to complete
      if (tokenExtracted) {
        console.log('⏰ Waiting for token storage to complete...')
        await new Promise(resolve => setTimeout(resolve, 300)) // Increased delay
      }
      
      // Then check authentication status
      console.log('🔍 Starting auth refresh...')
      await refreshAuth()
    }
    
    // Add error handling for initialization
    initializeAuth().catch(error => {
      console.error('❌ Auth initialization failed:', error)
      setAuthState({
        isAuthenticated: false,
        loading: false,
        error: 'Failed to initialize authentication'
      })
    })
  }, [])

  const contextValue: AuthContextType = {
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    logout: handleLogout,
    refresh: refreshAuth,
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}